import ExcelJS from "exceljs";
import type { ProjectVM } from "../pages/ProjectTablePage/types";

type Locale = "ru" | "kk" | "en";

interface Labels {
  date: string;
  company: string;
  category: string;
  income: string;
  expense: string;
  balance: string;
  total: string;
  grandTotal: string;
  initialBalance: string;
}

const LABELS: Record<Locale, Labels> = {
  ru: {
    date: "Дата",
    company: "Контрагент",
    category: "Статья",
    income: "Приход",
    expense: "Расход",
    balance: "Баланс",
    total: "Итого по проекту",
    grandTotal: "ИТОГО",
    initialBalance: "Начальный баланс",
  },
  kk: {
    date: "Күні",
    company: "Контрагент",
    category: "Бап",
    income: "Кіріс",
    expense: "Шығыс",
    balance: "Баланс",
    total: "Жоба бойынша барлығы",
    grandTotal: "БАРЛЫҒЫ",
    initialBalance: "Бастапқы қалдық",
  },
  en: {
    date: "Date",
    company: "Counterparty",
    category: "Item",
    income: "Income",
    expense: "Expense",
    balance: "Balance",
    total: "Project Total",
    grandTotal: "GRAND TOTAL",
    initialBalance: "Initial balance",
  },
};

const C = {
  dark: "FF404040",
  medium: "FF595959",
  project: "FFD6DCE4",
  income: "FFF2F2F2",
  group: "FFE7E6E6",
  item: "FFFFFFFF",
  subtotal: "FFD6DCE4",
  white: "FFFFFFFF",
  black: "FF000000",
} as const;

const NUM_FMT = "#,##0.00";

function parseNum(value: string | undefined): number {
  if (!value) return 0;
  const n = parseFloat(value.replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function numVal(value: string | undefined): number | null {
  const n = parseNum(value);
  return n !== 0 ? n : null;
}

function fill(argb: string): ExcelJS.Fill {
  return { type: "pattern", pattern: "solid", fgColor: { argb } };
}

function applyRow(
  row: ExcelJS.Row,
  opts: { bgColor: string; fgColor?: string; bold?: boolean; outlineLevel?: number; height?: number; italic?: boolean },
) {
  row.fill = fill(opts.bgColor);
  row.font = {
    bold: opts.bold ?? false,
    italic: opts.italic ?? false,
    color: { argb: opts.fgColor ?? C.black },
    size: 12,
    name: "Calibri",
  };
  if (opts.outlineLevel !== undefined) row.outlineLevel = opts.outlineLevel;
  if (opts.height !== undefined) row.height = opts.height;
  row.eachCell({ includeEmpty: true }, (cell) => {
    cell.border = { bottom: { style: "thin", color: { argb: "FFD9D9D9" } } };
  });
}

function applyNumericCells(row: ExcelJS.Row, colIndices: number[]) {
  for (const col of colIndices) {
    const cell = row.getCell(col);
    if (typeof cell.value === "number") {
      cell.numFmt = NUM_FMT;
      cell.alignment = { horizontal: "right" };
    }
  }
}

export async function generateProjectTableXlsx(projects: ProjectVM[], locale: Locale): Promise<void> {
  const L = LABELS[locale];

  const wb = new ExcelJS.Workbook();
  wb.creator = "SEN Group";
  wb.created = new Date();

  const ws = wb.addWorksheet("Projects", {
    properties: { outlineLevelRow: 2 },
    views: [{ showGridLines: true }],
  });

  ws.columns = [
    { width: 14 },
    { width: 40 },
    { width: 32 },
    { width: 18 },
    { width: 18 },
    { width: 18 },
    { width: 4 },
  ];

  const titleRow = ws.addRow([L.grandTotal, "", "", "", "", "", ""]);
  ws.mergeCells(`A${titleRow.number}:F${titleRow.number}`);
  applyRow(titleRow, { bgColor: C.dark, fgColor: C.white, bold: true, height: 22 });
  titleRow.getCell(1).alignment = { horizontal: "center", vertical: "middle" };
  titleRow.getCell(1).font = { bold: true, color: { argb: C.white }, size: 14, name: "Calibri" };

  const headerRow = ws.addRow([L.date, L.company, L.category, L.income, L.expense, L.balance, ""]);
  applyRow(headerRow, { bgColor: C.medium, fgColor: C.white, bold: true, height: 18 });
  for (let col = 4; col <= 6; col++) {
    headerRow.getCell(col).alignment = { horizontal: "right" };
  }

  let grandIncome = 0;
  let grandExpense = 0;

  for (const project of projects) {
    const pIncome = parseNum(project.totalIncome);
    const pExpense = parseNum(project.totalExpense);
    const pBalance = parseNum(project.balance);
    grandIncome += pIncome;
    grandExpense += pExpense;

    const projectRow = ws.addRow([
      "",
      project.label,
      "",
      pIncome !== 0 ? pIncome : null,
      pExpense !== 0 ? pExpense : null,
      pBalance !== 0 ? pBalance : null,
      "",
    ]);
    applyRow(projectRow, { bgColor: C.project, bold: true, height: 18 });
    applyNumericCells(projectRow, [4, 5, 6]);

    if (parseNum(project.initialBalance) !== 0) {
      const initRow = ws.addRow(["", L.initialBalance, "", numVal(project.initialBalance), null, null, ""]);
      applyRow(initRow, { bgColor: C.income, outlineLevel: 1, italic: true });
      applyNumericCells(initRow, [4]);
    }

    for (const group of project.incomeGroups) {
      const groupRow = ws.addRow(["", group.label, "", numVal(group.total), null, null, ""]);
      applyRow(groupRow, { bgColor: C.group, bold: true, outlineLevel: 1, height: 16 });
      applyNumericCells(groupRow, [4]);
      for (const record of group.records) {
        const recRow = ws.addRow([record.date, record.company, record.category, numVal(record.amount), null, null, ""]);
        applyRow(recRow, { bgColor: C.item, outlineLevel: 2 });
        applyNumericCells(recRow, [4]);
      }
    }

    for (const group of project.expenseGroups) {
      const groupRow = ws.addRow(["", group.label, "", null, numVal(group.total), null, ""]);
      applyRow(groupRow, { bgColor: C.group, bold: true, outlineLevel: 1, height: 16 });
      applyNumericCells(groupRow, [5]);
      for (const record of group.records) {
        const recRow = ws.addRow([record.date, record.company, record.category, null, numVal(record.amount), null, ""]);
        applyRow(recRow, { bgColor: C.item, outlineLevel: 2 });
        applyNumericCells(recRow, [5]);
      }
    }

    const subtotalRow = ws.addRow([
      "",
      L.total,
      "",
      pIncome !== 0 ? pIncome : null,
      pExpense !== 0 ? pExpense : null,
      pBalance !== 0 ? pBalance : null,
      "",
    ]);
    applyRow(subtotalRow, { bgColor: C.subtotal, bold: true, outlineLevel: 1, height: 18 });
    applyNumericCells(subtotalRow, [4, 5, 6]);
  }

  const grandBalance = grandIncome - grandExpense;
  const grandRow = ws.addRow([
    "",
    L.grandTotal,
    "",
    grandIncome !== 0 ? grandIncome : null,
    grandExpense !== 0 ? grandExpense : null,
    grandBalance !== 0 ? grandBalance : null,
    "",
  ]);
  applyRow(grandRow, { bgColor: C.dark, fgColor: C.white, bold: true, height: 20 });
  applyNumericCells(grandRow, [4, 5, 6]);

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = "project-table.xlsx";
  anchor.click();
  URL.revokeObjectURL(url);
}
